import React from 'react';
import {
  TextField,
  Autocomplete,
  InputAdornment,
  Chip,
  Box,
  createFilterOptions
} from "@mui/material";
import {
  FaFilter, FaRedo, FaSearch,
  FaBuilding, FaProjectDiagram, FaUser, FaClock, FaFlag, FaUsers
} from "react-icons/fa";
import CustomButton from "../Custom/CustomButton";
import DateRangeFilter from "./DateRangeFilter";

const employeeFilterOptions = createFilterOptions({
  stringify: (o) =>
    `${o.first_name || o.FirstName || o.firstName || ""} ${o.last_name || o.LastName || o.lastName || ""} ${o.email || o.Email || ""}`.trim(),
});

const TaskPageHeader = ({
  title,
  buttons = [],
  statusSummary = [],
  currentStatus = "All",
  onStatusFilter,
  tasks = [],
  filters = {},
  onFilterChange,
  globalFilter = "",
  onGlobalFilterChange,
  onApply,
  onReset,
  activePills = [],
  statusCounts = null,
  isApplying = false
}) => {
  const normalizeStatusKey = (label = "") => {
    const l = String(label || "").trim().toLowerCase();
    if (!l || l === "all") return "all";
    if (l === "wip" || l === "in progress" || l === "in-progress") return "wip";
    return l.replace(/\s+/g, "_");
  };

  const getStatusCount = (label) => {
    const apiCounts = statusCounts && typeof statusCounts === "object" ? statusCounts : null;
    if (apiCounts) {
      const normalizedCounts = {};
      Object.keys(apiCounts).forEach(key => {
        normalizedCounts[key.toLowerCase()] = apiCounts[key];
      });

      if (label === "All") {
        return statusSummary
          .filter((card) => card.label !== "All")
          .reduce((sum, card) => sum + Number(normalizedCounts[normalizeStatusKey(card.label)] || 0), 0);
      }
      return Number(normalizedCounts[normalizeStatusKey(label)] || 0);
    }
    return tasks.filter((t) => label === "All" || (t.status || "Pending") === label).length;
  };

  return (
    <div className="task-page-header-container">
      {/* Row 1 (Title + Buttons) */}
      <div className="task-page-header-top">
        <h2 className="task-page-title">{title}</h2>
        <div className="task-header-btns">
          {buttons.map((btn, idx) => (
            <CustomButton
              key={idx}
              variant={btn.variant || "contained"}
              color={btn.color || "error"}
              startIcon={btn.icon}
              size="small"
              className="compact-header-btn"
              onClick={btn.onClick}
              loading={btn.loading}
              disabled={btn.disabled || isApplying}
              fullWidth={false}
              sx={{ textTransform: 'none', borderRadius: '6px', whiteSpace: 'nowrap', minWidth: 'max-content', ...btn.sx }}
            >
              {btn.label}
            </CustomButton>
          ))}
        </div>
      </div>

      <div className="task-page-filters-row">
        {/* Filters Row */}
        <div className="responsive-filters-grid">
          {filters.departments && (
            <Autocomplete
              className="filter-autocomplete-input"
              size="small"
              options={filters.departments.options}
              getOptionLabel={(o) => o.name || ""}
              isOptionEqualToValue={(opt, val) => (opt.id || opt.value) === (val.id || val.value)}
              value={filters.departments.value}
              onChange={(_, v) => onFilterChange("department", v)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="All Departments"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start" sx={{ pl: 0.5 }}>
                          <FaBuilding color="#888" size={14} />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          )}
          {filters.projects && (
            <Autocomplete
              className="filter-autocomplete-input"
              size="small"
              options={filters.projects.options}
              getOptionLabel={(o) => o.name || ""}
              isOptionEqualToValue={(opt, val) => (opt.id || opt.value) === (val.id || val.value)}
              value={filters.projects.value}
              onChange={(_, v) => onFilterChange("project", v)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="All Projects"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start" sx={{ pl: 0.5 }}>
                          <FaProjectDiagram color="#888" size={14} />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          )}
          {filters.groups && (
            <Autocomplete
              className="filter-autocomplete-input"
              size="small"
              options={filters.groups.options}
              getOptionLabel={(o) => o?.groupName || o?.name || ""}
              isOptionEqualToValue={(opt, val) => (opt.id || opt.value) === (val.id || val.value)}
              value={filters.groups.value}
              onChange={(_, v) => onFilterChange("group", v)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="All Groups"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start" sx={{ pl: 0.5 }}>
                          <FaUsers color="#888" size={14} />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          )}
          {filters.employees && (
            <Autocomplete
              className="filter-autocomplete-input"
              size="small"
              options={filters.employees.options}
              filterOptions={employeeFilterOptions}
              getOptionLabel={(o) => {
                const fn = o.first_name || o.FirstName || o.firstName || "";
                const ln = o.last_name || o.LastName || o.lastName || "";
                const name = `${fn} ${ln}`.trim();
                return name || o.email || o.Email || "";
              }}
              isOptionEqualToValue={(opt, val) => (opt.id || opt.userId || opt.value) === (val.id || val.userId || val.value)}
              value={filters.employees.value}
              onChange={(_, v) => onFilterChange("employee", v)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="All Employees"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start" sx={{ pl: 0.5 }}>
                          <FaUser color="#888" size={14} />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          )}
          {filters.ageing && (
            <Autocomplete
              className="filter-autocomplete-input"
              size="small"
              options={filters.ageing.options}
              getOptionLabel={(o) => o.label || ""}
              isOptionEqualToValue={(opt, val) => (opt.id || opt.value) === (val.id || val.value)}
              value={filters.ageing.value}
              onChange={(_, v) => onFilterChange("ageing", v)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Over Due (All)"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start" sx={{ pl: 0.5 }}>
                          <FaClock color="#888" size={14} />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          )}
          {filters.priority && (
            <Autocomplete
              className="filter-autocomplete-input"
              size="small"
              options={filters.priority.options}
              getOptionLabel={(o) => o.label || ""}
              isOptionEqualToValue={(opt, val) => (opt.id || opt.value) === (val.id || val.value)}
              value={filters.priority.value}
              onChange={(_, v) => onFilterChange("priority", v)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Priority (All)"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start" sx={{ pl: 0.5 }}>
                          <FaFlag color="#888" size={14} />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          )}
          {filters.targetEndDate !== undefined && (
            <DateRangeFilter
              value={filters.targetEndDate.value || {}}
              onChange={(rangeVal) => onFilterChange('targetEndDate', rangeVal)}
            />
          )}
        </div>
        {/* Row 4 (Search & Buttons) */}
        <div className="task-filters-search-row">
          <TextField
            className="compact-header-input"
            placeholder="Search tasks by name..."
            size="small"
            value={globalFilter}
            onChange={(e) => onGlobalFilterChange && onGlobalFilterChange(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && onApply) onApply(); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ pl: 0.5 }}>
                  <FaSearch color="#aaa" size={14} />
                </InputAdornment>
              ),
            }}
          />
          <div className="task-filter-btns">
            <CustomButton
              variant="outlined"
              color="error"
              startIcon={<FaFilter size={14} />}
              size="small"
              className="compact-header-btn"
              onClick={onApply}
              loading={isApplying}
              loadingText="Applying..."
              fullWidth={false}
              sx={{
                textTransform: 'none',
                borderRadius: '6px',
                border: '1px solid #d32f2f',
                color: '#d32f2f',
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(211, 47, 47, 0.04)',
                  borderColor: '#d32f2f'
                }
              }}
            >
              Apply
            </CustomButton>
            <CustomButton
              variant="contained"
              color="inherit"
              startIcon={<FaRedo size={14} />}
              size="small"
              className="compact-header-btn"
              onClick={onReset}
              disabled={isApplying}
              fullWidth={false}
              sx={{
                textTransform: 'none',
                borderRadius: '6px',
                backgroundColor: '#f1f3f5',
                color: '#111827',
                border: '1px solid #e0e4e8',
                '&:hover': { backgroundColor: '#e5e7eb', borderColor: '#d1d5db' }
              }}
            >
              Reset
            </CustomButton>
          </div>
        </div>
      </div>

      {/* Row 5: Active filter chips */}
      {activePills.length > 0 && activePills.some(p => !p.label?.startsWith("Status:")) && (
        <Box sx={{ display: "flex", gap: 1, pb: 1, flexWrap: "wrap" }}>
          {activePills
            .filter((pill) => !pill.label?.startsWith("Status:"))
            .map((pill, idx) => (
              <Chip
                key={idx}
                label={pill.label}
                size="small"
                onDelete={pill.onDelete}
                sx={{
                  borderRadius: "20px",
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  backgroundColor: "#d32f2f",
                  color: "#fff",
                  border: "1px solid #d32f2f",
                  boxShadow: "0 2px 6px rgba(211, 47, 47, 0.3)",
                  "& .MuiChip-deleteIcon": {
                    color: "rgba(255, 255, 255, 0.8)",
                    fontSize: "16px",
                    "&:hover": { color: "#fff" },
                  },
                  "& .MuiChip-label": {
                    px: 1.5,
                  },
                }}
              />
            ))}
        </Box>
      )}

      {/* Row 1.5 (Status Pills) */}
      <div className="status-pills-row">
        {statusSummary.map((card) => (
          <button
            key={card.label}
            className={`status-pill${currentStatus === card.label ? " active" : ""}${isApplying ? " disabled-pill" : ""}`}
            onClick={() => !isApplying && onStatusFilter && onStatusFilter(card.label)}
          >
            {card.label} <span className="status-pill-count">{getStatusCount(card.label)}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default TaskPageHeader;
